import React from 'react';
import { SvgIcon } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import { Button, usePermissions, useTranslate } from 'react-admin';
import { Confirmation, EditButton, TextField } from '~/components';
import DeleteButton from '~/components/DeleteButton';
import SaveButton from '~/components/SaveButton';
import { ArrowForwardCircleIcon } from '~/img';
import { history } from '~/App';
import { time } from '~/utils';
import palette from '~/theme/palette';
import { useEditMode } from '~/hooks/useEditModeContext';
import { draftIdPostfix, useCreateDraft, useDiscardDraft, useSubmitDraft } from '~/hooks/draft';
import { useDraft } from '~/hooks/useDraftContext';
import { useApprovalCase } from '~/hooks/useApprovalCaseContext';
import { draftPermissions } from '~/utils/recordPages';

const useStyles = makeStyles(() => ({
  rightContent: {
    flexGrow: 0,
    flexShrink: 0,
    width: '300px',
    minWidth: '300px',
    alignSelf: 'center',
    marginTop: '-8px',
    /* eslint-disable no-useless-computed-key */
    ['@media (min-width: 1280px)']: {
      width: '360px',
    },
  },
  draftMode: {
    backgroundColor: palette.draft.base,
  },
  rightContainer: {
    padding: '8px',
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
}));

interface Props {
  record: any;
}

const DraftPanel = ({ record }: Props) => {
  const classes = useStyles();
  const { editMode } = useEditMode();
  const { isAvailable, page, recordId, draft, isLoadingDraft } = useDraft();
  const {
    pendingCase,
    isLoading: isLoadingCase,
    refetch: refetchApprovalCases,
  } = useApprovalCase();

  const { permissions } = usePermissions();
  const { update, create } = draftPermissions(page);
  const hasPermissionEdit = permissions?.includes(update);
  const hasPermissionCreate = permissions?.includes(create);
  const canManageDraft = draft?.isUpdateDraft ? hasPermissionEdit : hasPermissionCreate;

  const translate = useTranslate();
  const t = (key: string, arg?: any): string => translate(`components.RecordTitle.${key}`, arg);

  const resource = page?.resource;
  const recordPath = `/${resource}/${recordId}${page?.tabPath ?? '/show'}`;
  const draftPath = `/${resource}/${recordId}${draftIdPostfix}${page?.tabPath ?? '/show'}`;

  const { mutateAsync: createDraft } = useCreateDraft();
  const onCreateDraft = () => {
    createDraft({ resource: resource!, record }).then((res) => {
      history.push(`/${resource}/${res?.id}${draftIdPostfix}${page?.tabPath ?? '/show'}`);
    });
  };

  const { mutateAsync: submitMutate, isLoading: isSubmitLoading } = useSubmitDraft();
  const onSubmitDraft = () => {
    submitMutate({ resource: resource!, draftId: `${recordId}${draftIdPostfix}` }).then(() => {
      refetchApprovalCases();
      history.push(recordPath);
    });
  };

  const { mutateAsync: discardMutate, isLoading: isDiscardLoading } = useDiscardDraft();
  const onDiscardDraft = () => {
    discardMutate({ resource: resource!, draftId: `${recordId}${draftIdPostfix}` }).then(() => {
      refetchApprovalCases();
      history.push(draft?.isUpdateDraft ? recordPath : `/${resource}/drafts`);
    });
  };

  if (!isAvailable) {
    return null;
  }

  // PendingCaseNotice has the page instead; silent while loading so nothing flashes.
  if (isLoadingCase || pendingCase) {
    return null;
  }

  // offer to create new draft
  if (editMode === 'VIEW' && !isLoadingDraft && !draft && hasPermissionEdit) {
    return (
      <div className={classes.rightContent}>
        <Paper>
          <Grid className={classes.rightContainer} container justify='space-between'>
            <Grid container justify='space-between'>
              <Grid item>
                <TextField>{t('proposeChange')}</TextField>
                <TextField label={t('draftWillBeCreated')}> </TextField>
              </Grid>
              <Grid item>
                <EditButton label={translate(`ra.action.edit`)} onClick={onCreateDraft} />
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }

  // offer to edit existing draft
  if (editMode === 'VIEW' && !isLoadingDraft && draft && hasPermissionEdit) {
    return (
      <div className={classes.rightContent}>
        <Paper className={classes.draftMode}>
          <Grid className={classes.rightContainer} container justify='space-between'>
            <Grid container justify='space-between'>
              <Grid item>
                <TextField>{t('haveDraft')}</TextField>
                <TextField label={`${t('created')} ${dateFormat(draft.createdAt)}`}> </TextField>
              </Grid>
              <Grid item>
                <Button label={t('viewDraft')} onClick={() => history.push(draftPath)}>
                  <SvgIcon>
                    <ArrowForwardCircleIcon />
                  </SvgIcon>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }

  // editing current draft
  if (editMode === 'DRAFT') {
    return (
      <div className={classes.rightContent}>
        <Paper className={classes.draftMode}>
          <Grid className={classes.rightContainer} container justify='space-between'>
            <Grid container justify='space-between' alignItems='center'>
              <Grid item>
                <TextField>{t('myDraft')}</TextField>
                {draft && (
                  <TextField label={`${t('created')} ${dateFormat(draft.createdAt)}`}> </TextField>
                )}
              </Grid>
              {!isLoadingDraft && canManageDraft && (
                <Grid item>
                  <Grid container spacing={1} alignItems='center'>
                    <Grid item>
                      <Confirmation
                        onConfirm={onDiscardDraft}
                        confirmationSettings={{
                          variant: 'modal',
                          modal: {
                            heading: t('discardConfirmationTitle'),
                            content: t('discardConfirmationBody'),
                          },
                        }}
                      >
                        <DeleteButton
                          label={t('discard')}
                          loading={isSubmitLoading || isDiscardLoading}
                        />
                      </Confirmation>
                    </Grid>
                    <Grid item>
                      <Confirmation
                        onConfirm={onSubmitDraft}
                        confirmationSettings={{
                          variant: 'modal',
                          modal: {
                            heading: t('submitConfirmationTitle'),
                            content: t('submitConfirmationBody'),
                          },
                        }}
                      >
                        <SaveButton
                          label={t('submit')}
                          size='small'
                          loading={isSubmitLoading || isDiscardLoading}
                        />
                      </Confirmation>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }

  return null;
};

const dateFormat = (dateString: string) => {
  return `${time(dateString).format('l')} ${time(dateString).format('LT')}`;
};

export default DraftPanel;
